/**
 * A person's name: first and last, stored separately (docs/DECISIONS.md D-002).
 *
 * `name` is DERIVED — "First Last" — and written at the same moment as the two
 * halves, only ever through `nameFields`. It stays because everything that
 * shows a person (the Users list, search, the activity log, emails, the
 * assistant, Better Auth itself) reads it. Nothing sets `name` on its own, so
 * the three can't drift.
 */
import { z } from 'zod'

/**
 * The two halves as Better Auth `user.additionalFields` — declared once, read
 * by the server (src/adapters/auth/server.ts) and the browser client alike.
 * Optional to Better Auth only because accounts from before the split may lack
 * them; every form requires both (personNameSchema).
 */
export const personNameAuthFields = {
  firstName: { type: 'string', required: false },
  lastName: { type: 'string', required: false },
} as const

/** One half of a name: required, trimmed, a sensible length. */
export const namePart = (missing: string) =>
  z.string().trim().min(1, missing).max(100, 'Use 100 characters or fewer.')

/** What a person enters wherever they give or change their name. Checked on the server. */
export const personNameSchema = z.object({
  firstName: namePart('Enter a first name.'),
  lastName: namePart('Enter a last name.'),
})
export type PersonName = z.infer<typeof personNameSchema>

/** "Jordan" + "Lee" → "Jordan Lee". Either half may be empty for old, backfilled rows. */
export function fullName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ')
}

/** The three columns, together — the only way a name is written. */
export function nameFields(firstName: string, lastName: string) {
  const first = firstName.trim()
  const last = lastName.trim()
  return { firstName: first, lastName: last, name: fullName(first, last) }
}

/**
 * Split an old single `name` for the backfill: everything before the first
 * space is the first name, the rest the last name; no space, no last name.
 * The same rule as the SQL in drizzle/0018_person_names.sql — a test holds
 * the two together.
 */
export function splitName(name: string): { firstName: string; lastName: string } {
  const trimmed = name.trim()
  const space = trimmed.indexOf(' ')
  if (space === -1) return { firstName: trimmed, lastName: '' }
  return { firstName: trimmed.slice(0, space), lastName: trimmed.slice(space + 1).trim() }
}
